import { IconRalphWiggum } from "@/assets/icons/IconRalphWiggum";
import { Markdown } from "@/components/ai/chat/Markdown";
import { cn } from "@/components/podkit/lib/cn";
import type { UserMessage } from "@/hooks/use-stream-conversation";
import { IconClock } from "central-icons/IconClock";
import { IconSearchlinesSparkle } from "@central-icons-react/round-outlined-radius-2-stroke-1.5/IconSearchlinesSparkle";
import { IconCodeAssistant } from "@central-icons-react/round-outlined-radius-2-stroke-1.5/IconCodeAssistant";
import { useEffect, useState, type FC } from "react";

// Command prefixes for autonomous modes
const RALPH_COMMAND = "/ona:ralph";
const SPEC_COMMAND = "/ona:spec";

type AgentModePrefix = "ralph" | "spec" | "agent";

/**
 * Parses agent mode command prefix from content.
 * Returns both the detected mode and the content with prefix stripped.
 */
function parseAgentModePrefix(content: string): { mode: AgentModePrefix; strippedContent: string } {
    // Check Ralph prefix
    if (content.startsWith(RALPH_COMMAND)) {
        const remainder = content.slice(RALPH_COMMAND.length);
        if (remainder === "" || remainder.startsWith(" ")) {
            return { mode: "ralph", strippedContent: remainder.trimStart() };
        }
    }
    // Check Spec prefix
    if (content.startsWith(SPEC_COMMAND)) {
        const remainder = content.slice(SPEC_COMMAND.length);
        if (remainder === "" || remainder.startsWith(" ")) {
            return { mode: "spec", strippedContent: remainder.trimStart() };
        }
    }
    return { mode: "agent", strippedContent: content };
}

const modeConfig = {
    agent: {
        Icon: IconCodeAssistant,
        iconColorClass: "text-content-brand",
        bgColorClass: "bg-surface-brand-subtle",
        borderColorClass: "border-r-surface-brand-subtle",
    },
    spec: {
        Icon: IconSearchlinesSparkle,
        iconColorClass: "text-content-brand-accent-01",
        bgColorClass: "bg-surface-brand-accent-01-subtle",
        borderColorClass: "border-r-surface-brand-accent-01-subtle",
    },
    ralph: {
        Icon: IconRalphWiggum,
        iconColorClass: "text-content-orange",
        bgColorClass: "bg-surface-orange",
        borderColorClass: "border-r-surface-orange",
    },
};

const AgentModeIcon: FC<{ mode: AgentModePrefix }> = ({ mode }) => {
    const { Icon, iconColorClass, bgColorClass } = modeConfig[mode];

    return (
        <div
            className={cn("mt-[7px] flex size-6 shrink-0 items-center justify-center", bgColorClass)}
            style={{ borderRadius: "32px 32px 32px 6px" }}
        >
            <Icon size={14} className={iconColorClass} />
        </div>
    );
};

const UserImage: FC<{ data: Uint8Array; mimeType: string }> = ({ data, mimeType }) => {
    const [imageUrl, setImageUrl] = useState<string>("");

    useEffect(() => {
        if (!data || data.length === 0) {
            return;
        }

        // Handle case where data might be a regular array or object instead of Uint8Array
        let blobData: BlobPart;
        if (data instanceof Uint8Array) {
            blobData = data;
        } else if (Array.isArray(data)) {
            blobData = new Uint8Array(data);
        } else if (typeof data === "object" && data !== null) {
            // Protobuf-es might return an object with numeric keys
            const values = Object.values(data as Record<string, number>);
            blobData = new Uint8Array(values);
        } else {
            return;
        }

        const blob = new Blob([blobData], { type: mimeType || "image/png" });
        const url = URL.createObjectURL(blob);
        setImageUrl(url);

        // Cleanup on unmount only, not on re-render
        return () => {
            URL.revokeObjectURL(url);
        };
    }, [data, mimeType]);

    if (!imageUrl) {
        return null;
    }

    return (
        <img
            src={imageUrl}
            alt="User uploaded image"
            className="max-h-64 max-w-full rounded-lg object-contain"
        />
    );
};

export const UserMsg: FC<{
    message: UserMessage;
    isPending: boolean;
}> = ({ message, isPending }) => {
    // Check if any fragment has an agent mode prefix
    const agentMode = message.fragments.reduce<AgentModePrefix>((found, fragment) => {
        if (found !== "agent") return found;
        if (fragment.type === "text") {
            return parseAgentModePrefix(fragment.content).mode;
        }
        return "agent";
    }, "agent");

    const { borderColorClass } = modeConfig[agentMode];

    return (
        // the class user-message is used within the markdown component to style some markdown elements differently when presented
        // inside a user message.
        <div
            className="user-message flex w-fit max-w-full items-start gap-2 self-end"
            data-message-id={`${message.type}-${message.id}`}
        >
            <div
                className={cn(
                    "min-w-0 flex-1 rounded-xl border border-border-subtle border-r-[1.5px] bg-surface-muted px-3 py-2 text-base text-content-primary shadow-sm transition-opacity",
                    borderColorClass,
                    {
                        "opacity-50": isPending,
                    },
                )}
            >
                <div className="flex gap-2">
                    {isPending && <IconClock className="mt-[1px] shrink-0 text-content-primary" size="14" />}
                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                        {message.fragments.map((fragment) => {
                            switch (fragment.type) {
                                case "text": {
                                    const { strippedContent } = parseAgentModePrefix(fragment.content);
                                    return <Markdown content={strippedContent} key={fragment.id} />;
                                }
                                case "image": {
                                    return (
                                        <UserImage
                                            key={fragment.id}
                                            data={fragment.data}
                                            mimeType={fragment.mimeType}
                                        />
                                    );
                                }
                            }
                        })}
                    </div>
                </div>
            </div>
            <AgentModeIcon mode={agentMode} />
        </div>
    );
};
